import { useEffect, useRef, memo, useMemo, useCallback, useState } from "react";
import DataGrid, {
  Column,
  Format,
  Grouping,
  GroupPanel,
  Paging,
  SearchPanel,
  Summary,
  GroupItem,
  Export,
  FilterRow,
  HeaderFilter,
  Pager,
  Selection,
  Item,
  Toolbar,
  StateStoring,
  ColumnChooser,
} from "devextreme-react/data-grid";
import SelectBox from "devextreme-react/select-box";

import notify from "devextreme/ui/notify";
import { LoadPanel } from "devextreme-react/load-panel";
import { fetchWithToken, useFetch } from "../../../hooks/useFetch";
import { useAuth } from "src/contexts/auth";
// import "./run-list.css"
import { gradeMetricForRun } from "src/utils/run-grading";

import kits from "src/pages/report/sequencing-kits.json";

const filtNeg999s = (data) => {
  if (data.value === -999) {
    return <div></div>;
  }
  return <div>{data.text}</div>;
};

const sequencingKitLookup = (pn) => {
  const obj = kits.find((v) => v.partNumber === pn);
  if (obj) return obj.name;
  return "N/A";
};

const targetedReadsLookup = (pn) => {
  const obj = kits.find((v) => v.partNumber === pn);
  if (obj) return obj.targetedReads;
  return "N/A";
};

const cnstMode = { mode: "single" };
const fields = [
  { name: "Last Week", value: -7 },
  { name: "Last Month", value: 1 },
  { name: "Last 3 Months", value: 3 },
  { name: "Last 6 Months", value: 6 },
  { name: "Last Year", value: 12 },
  { name: "All", value: 0 },
];

const groupCellTemplate = (elem, data) => {
  var formattedValue = `${data.displayValue} (${data.data.aggregates} runs)   ${
    data.groupContinuesMessage ? `  (${data.groupContinuesMessage})` : ""
  }`;
  elem.append(formattedValue);
};

const renderGridCell = (data) => {
  return (
    <div>
      <div
        className="badge"
        onClick={() => alert(data.data.runID)}
        style={{
          display:
            data.data.runID === "6345e6054e6fad0dd71522e1" ? null : "none",
        }}
      >
        10
      </div>
      <div>{data.text}</div>
    </div>
  );
};

const pagesizes = [20, 100, 500];

// Export is intentionally bounded: it respects the grid's current filter and
// never writes more than EXPORT_ROW_LIMIT rows, so a full-table dump can't
// happen in a single click while users can still export a filtered batch.
const EXPORT_ROW_LIMIT = 5000;

const csvCell = (val) => {
  if (val === null || val === undefined) return "";
  let s = typeof val === "object" ? JSON.stringify(val) : String(val);
  if (/[",\n\r]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
};

const buildCsv = (columns, rows) => {
  const header = columns
    .map((c) => csvCell(c.caption ?? c.dataField))
    .join(",");
  const lines = rows.map((row) =>
    columns
      .map((c) => {
        const v = c.calculateCellValue
          ? c.calculateCellValue(row)
          : row[c.dataField];
        return csvCell(v);
      })
      .join(","),
  );
  return [header, ...lines].join("\r\n");
};

const downloadCsv = (csv, fileName) => {
  // Prepend a UTF-8 BOM so Excel opens non-ASCII characters correctly.
  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const colfixing = { enabled: true };

const customizedDateText = (e) => {
  if (e.value.getFullYear() < 2020) return "";
  return e.valueText;
};

export const getRecipeBundle = (run) => {
  if (run.recipeValues) {
    const recipeValuesSplit = run.recipeValues.split("^^^");
    for (const value of recipeValuesSplit) {
      const splitValue = value.split("###");
      if (splitValue[0] === "RecipesBundleName") return splitValue[1];
    }
  }

  return "";
};

// Decorate the trimmed rows from the list endpoint with the fields the grid
// derives client-side (kit lookups + the recipe bundle fallback).
const decorateRuns = (rows) => {
  for (let i = 0; i < rows.length; i++) {
    rows[i].TargetedReads = targetedReadsLookup(rows[i].reagentPartNumber);
    rows[i].SequencingKit = sequencingKitLookup(rows[i].reagentPartNumber);
    rows[i].RecipeBundle = rows[i].recipeBundle ?? getRecipeBundle(rows[i]);
  }
  return rows;
};

export default memo((props) => {
  const customerOnly = props.customerOnly;
  const { getAccessTokenSilently } = useAuth();
  const [numMonths, setNumMonths] = useState(-7);
  const grid = useRef();

  // The list endpoint returns trimmed rows (no recipeValues/description/
  // runNotes); the grid does its own client-side paging/sorting/filtering/
  // grouping, and the detail panel lazily fetches the full record via
  // /run/{id}/record when a row is selected.
  const queryPath =
    "/run?light&numMonths=" + numMonths + (customerOnly ? "&customer" : "");
  const { data: runsData, isLoading, refetch } = useFetch(queryPath);

  const runs = useMemo(
    () => (Array.isArray(runsData) ? decorateRuns(runsData) : []),
    [runsData],
  );

  // Bounded, filter-respecting export. We cancel DevExtreme's built-in export
  // (which would otherwise serialize the entire loaded table) and instead emit
  // a CSV of the rows matching the grid's current filter, capped at
  // EXPORT_ROW_LIMIT, over the currently visible columns.
  const onExporting = useCallback(async (e) => {
    e.cancel = true;
    const component = e.component;
    const ds = component.getDataSource();

    let loaded;
    try {
      loaded = await ds.store().load({
        filter: component.getCombinedFilter(true),
        sort: ds.sort(),
      });
    } catch (err) {
      notify("Export failed. Please try again.", "error", 4000);
      return;
    }

    const data = Array.isArray(loaded) ? loaded : loaded?.data ?? [];
    if (data.length === 0) {
      notify("No rows to export for the current filters.", "warning", 4000);
      return;
    }

    const rows = data.slice(0, EXPORT_ROW_LIMIT);
    const columns = component
      .getVisibleColumns()
      .filter((c) => c.dataField && c.allowExporting !== false);
    downloadCsv(buildCsv(columns, rows), "RunsTable.csv");

    if (data.length > rows.length) {
      notify(
        `Exported the first ${rows.length} of ${data.length} matching runs (export is capped at ${EXPORT_ROW_LIMIT}). Apply filters to narrow your results.`,
        "warning",
        8000,
      );
    }
  }, []);

  // Deep link (#/runs?r=<id>): show the run in the detail panel even if it is
  // not on the current grid page.
  useEffect(() => {
    const params = new URLSearchParams(
      window.location.hash.replace("#/runs", "").replace("#/customerRuns", ""),
    );
    const runID = params.get("r");
    if (runID && runID !== "null") {
      fetchWithToken(getAccessTokenSilently, "/run/" + runID + "/record")
        .then((rec) => {
          if (rec) props.runSelected(rec);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectionChanged = useCallback(
    (e) => {
      props.runSelected(e.selectedRowsData[0]);
    },
    [props],
  );

  const refreshOptions = useMemo(() => {
    return {
      icon: "refresh",
      hint: "Refresh",
      text: "",
      stylingMode: "icon",
      onClick: () => {
        refetch();
      },
    };
  }, [refetch]);

  const resetOptions = useMemo(() => {
    return {
      hint: "Reset to default table layout",
      text: "Reset",
      stylingMode: "text",
      onClick: () => {
        grid.current.instance.state({});
      },
    };
  }, []);

  const customizeText = useCallback((e) => {
    if (e.value <= 0) return "";
    return e.valueText;
  }, []);

  const customizeTextUpTime = useCallback((e) => {
    if (e.value <= 0) return "";
    const hours = e.value / 3600;
    const days = hours / 24;
    return days.toFixed(2) + " days";
  }, []);

  const cellPrepared = useCallback((e) => {
    if (e.rowType !== "data") return;
    const clr = gradeMetricForRun(
      e.column.name,
      e.value,
      e.data?.reagentPartNumber,
    );

    e.cellElement.style.backgroundColor = clr;
    if (!clr) e.cellElement.style.color = "black";
    else e.cellElement.style.color = "white";
  }, []);

  // The latest datagrid implementation with embedded refresh toolbar icon
  return (
    <div id="parentDivList" style={{ margin: 10 }}>
      <SelectBox
        value={numMonths}
        valueExpr="value"
        displayExpr="name"
        placeholder="Select a value..."
        showClearButton={false}
        dataSource={fields}
        text="Data to query"
        onValueChanged={(v) => {
          // Changing numMonths changes the query key, which refetches the list.
          setNumMonths(v.value);
        }}
        style={{ width: "150px" }}
      />

      <LoadPanel
        visible={isLoading}
        shading
        showIndicator
        message="Loading runs…"
        position={{ of: "#parentDivList" }}
      />

      <DataGrid
        ref={grid}
        className="mygrid"
        dataSource={runs}
        width="100%"
        height="90vh"
        keyExpr="runID"
        wordWrapEnabled
        showBorders
        showColumnLines
        showRowLines
        selection={cnstMode}
        allowColumnResizing
        allowColumnReordering
        columnResizingMode="widget"
        columnHidingEnabled
        columnAutoWidth
        // columnFixing={colfixing}
        onCellPrepared={cellPrepared}
        onSelectionChanged={onSelectionChanged}
        onExporting={onExporting}
      >
        <Export enabled />
        <GroupPanel visible />
        <SearchPanel visible />
        <Grouping autoExpandAll />
        <FilterRow visible />
        <ColumnChooser visible enabled />
        <HeaderFilter visible />
        <Selection mode={"single"} />
        <StateStoring
          enabled={false}
          type="localStorage"
          storageKey={customerOnly ? "customerRunsTablev14" : "runsTablev14"}
        />

        <Toolbar>
          <Item location="after" widget="dxButton" options={refreshOptions} />
          <Item name="columnChooserButton" />
          <Item name="groupPanel" />
          <Item name="searchPanel" />
          <Item name="exportButton" />
          {/* <Item location="before" widget="dxButton" options={spacerOptions} /> */}
          <Item location="before" widget="dxButton" options={resetOptions} />
        </Toolbar>

        <Column
          //   fixed
          dataField="runName"
          dataType="string"
          cellRender={renderGridCell}
        />
        <Column
          dataField="instrumentName"
          caption="instrument"
          dataType="string"
          groupCellTemplate={groupCellTemplate}
        />
        <Column
          dataField="model"
          dataType="string"
          groupCellTemplate={groupCellTemplate}
        />
        <Column
          dataField="instrumentSerialNumber"
          caption="Serial Number"
          dataType="string"
          groupCellTemplate={groupCellTemplate}
        />
        <Column
          dataField="instrumentNickname"
          dataType="string"
          groupCellTemplate={groupCellTemplate}
        />
        <Column
          dataField="country"
          dataType="string"
          groupCellTemplate={groupCellTemplate}
          allowEditing={false}
        />
        <Column
          dataField="city"
          dataType="string"
          groupCellTemplate={groupCellTemplate}
          allowEditing={false}
        />
        <Column
          dataField="Date"
          dataType={"date"}
          format={"MMM dd yyyy HH:mm"}
        />

        <Column dataField="side" />
        <Column
          dataField="lastStatusUpdated"
          dataType={"date"}
          format={"MMM dd yyyy HH:mm"}
        />
        <Column dataField="lastStatus" />

        <Column
          dataField="daysSinceLastMaintenanceWash"
          allowEditing={false}
          customizeText={customizeText}
        />

        <Column
          dataField="systemUpTimeInSeconds"
          allowEditing={false}
          customizeText={customizeTextUpTime}
          caption="System Up Time"
        />

        <Column
          dataField="runOrigin"
          dataType="string"
          groupCellTemplate={groupCellTemplate}
        />

        <Column
          dataField="q30"
          allowEditing={false}
          customizeText={customizeText}
        >
          <Format type="percent" precision={2} />
        </Column>
        <Column
          dataField="q40"
          allowEditing={false}
          customizeText={customizeText}
        >
          <Format type="percent" precision={2} />
        </Column>
        <Column
          dataField="q50"
          allowEditing={false}
          customizeText={customizeText}
        >
          <Format type="percent" precision={2} />
        </Column>
        <Column
          dataField="avgQ"
          allowEditing={false}
          customizeText={customizeText}
        >
          <Format type="fixedPoint" precision={1} />
        </Column>
        <Column
          dataField="indexAssignmentPercent"
          caption="Index Assignment (%)"
          allowEditing={false}
          customizeText={customizeText}
        >
          <Format type="fixedPoint" precision={2} />
        </Column>
        <Column
          dataField="error"
          allowEditing={false}
          customizeText={customizeText}
        >
          <Format type="percent" precision={4} />
        </Column>
        <Column
          dataField="poloniesRaw"
          caption="Polonies Raw (M)"
          customizeText={customizeText}
          allowEditing={false}
        >
          <Format type="fixedPoint" precision={0} />
        </Column>
        <Column
          dataField="poloniesPF"
          caption="Polonies PF (M)"
          customizeText={customizeText}
          allowEditing={false}
        >
          <Format type="fixedPoint" precision={0} />
        </Column>
        <Column
          dataField="runTime"
          caption="Run Time (Hrs)"
          allowEditing={false}
          customizeText={customizeText}
        >
          <Format type="fixedPoint" precision={2} />
        </Column>

        <Column dataField="RecipeBundle" allowEditing={false} />

        <Column dataField="tenantID" width={150} />
        <Column dataField="version" />
        <Column dataField="runFolderName" />
        <Column dataField="prepType" />
        <Column dataField="chemistryVersion" />
        <Column dataField="kitType" />
        <Column dataField="libraryPools" />
        <Column dataField="SequencingKit" allowEditing={false} />
        <Column dataField="panelID" allowEditing={false} />
        <Column dataField="TargetedReads" allowEditing={false} />
        <Column dataField="readOrder" />
        <Column dataField="highDensity" allowEditing={false} />
        <Column
          dataField="occupancyScore"
          allowEditing={false}
          cellRender={filtNeg999s}
        />
        <Column dataField="lowDiversity" allowEditing={false} />
        <Column dataField="seqCNNEnabled" allowEditing={false} />
        <Column dataField="shiftPMG" dataType="string" />
        <Column dataField="isTrainingRun" width={150} />
        <Column dataField="r1" />
        <Column dataField="r2" />
        <Column dataField="i1" />
        <Column dataField="i2" />
        <Column dataField="numTiles" />
        <Column dataField="tileSelectionModule" />

        <Column dataField="flowcellSerialNumber" />
        <Column dataField="flowcellPartNumber" />
        <Column dataField="flowcellLotNumber" />
        <Column
          customizeText={customizedDateText}
          dataField="flowcellExpiration"
          dataType={"date"}
          format={"MMM dd yyyy"}
        />

        <Column dataField="reagentSerialNumber" />
        <Column dataField="reagentPartNumber" />
        <Column dataField="reagentLotNumber" />
        <Column
          customizeText={customizedDateText}
          dataField="reagentExpiration"
          dataType={"date"}
          format={"MMM dd yyyy"}
        />

        <Column dataField="bufferSerialNumber" />
        <Column dataField="bufferPartNumber" />
        <Column dataField="bufferLotNumber" />
        <Column
          customizeText={customizedDateText}
          dataField="bufferExpiration"
          dataType={"date"}
          format={"MMM dd yyyy"}
        />
        <Column dataField="operatingMode" dataType="string" visible={false} />

        <Summary>
          <GroupItem
            column="instrumentName"
            summaryType="count"
            displayFormat="{0} runs"
          />
        </Summary>

        <Paging defaultPageSize={100} />
        <Pager
          showPageSizeSelector={true}
          allowedPageSizes={pagesizes}
          showInfo={true}
          showNavigationButtons={true}
        />
      </DataGrid>
    </div>
  );
});
